<script setup lang="ts">
import { toRef } from 'vue';

import VLoader from '@components/loader/VLoader.vue';
import VReportTabContent from '@modules/reports/components/VReportTabContent/VReportTabContent.vue';
import VReportTabFooter from '@modules/reports/components/VReportTabFooter/VReportTabFooter.vue';
import VReportTabs from '@modules/reports/components/VReportTabs/VReportTabs.vue';
import { useReportsMedia } from '@modules/reports/composables';

import type { IReportWidgetEmits, IReportWidgetProps } from './types';
import VReportInnerTabsMobile from '../VReportInnerTabsMobile/VReportInnerTabsMobile.vue';
import VReportTabsMobile from '../VReportTabsMobile/VReportTabsMobile.vue';
import { useReportWidget } from './composables';

const props = defineProps<IReportWidgetProps>();
const emit = defineEmits<IReportWidgetEmits>();

const {
  queryOptions,
  tabsData,
  tabsList,
  innerTabsList,
  activeTab,
  activeInnerTab,
  isStructureStatesAllowed,
  addTabBtnShortCode,
  isTabsLoading,
  isAddTabLoading,
  isRemoveTabLoading,
  isSegmentsType,
  innerTabFetching,
  onSelectTab,
  onSelectInnerTab,
  onToggleTab,
  onRemoveTab,
  onAddTab,
  changeTabName,
  changeStructureState,
  onInnerTabFetching,
} = useReportWidget(
  toRef(props, 'reportType'),
  toRef(props, 'widgetType'),
  toRef(props, 'defaultInnerTabs'),
  toRef(props, 'queryPermission'),
  toRef(props, 'structureStatesPermission'),
  toRef(props, 'translationKey'),
  emit,
);

const {
  isGreaterThenMd,
  selectLabel,
} = useReportsMedia();
</script>

<template>
  <div v-data-qa-auto-id="'VReportWidget'">
    <VLoader
      :loading="isTabsLoading"
      mode="structural"
      min-height="33.5px"
      spinner-size="small"
      spinner-wrapper-class="w-full"
    >
      <div>
        <VReportTabs
          v-if="isGreaterThenMd"
          :tabs="tabsList"
          :translation-key="translationKey"
          :is-loading="isTabsLoading"
          :add-label="$getTranslation(translationKey, addTabBtnShortCode)"
          :add-icon="isAddTabLoading ? 'pi pi-spin pi-spinner' : undefined"
          :add-no-pointer-events="isAddTabLoading || innerTabFetching"
          @select="onSelectTab"
          @expand-toggle="onToggleTab"
          @remove="onRemoveTab"
          @add="onAddTab"
        />
        <VReportTabsMobile
          v-else
          :tabs="tabsList"
          :translation-key="translationKey"
          :active-tab="activeTab"
          :select-label="selectLabel"
          :add-label="$getTranslation(translationKey, addTabBtnShortCode)"
          :is-add-loading="isAddTabLoading"
          :is-remove-loading="isRemoveTabLoading"
          @select="onSelectTab"
          @expand-toggle="onToggleTab"
          @remove="onRemoveTab"
          @add="onAddTab"
        />
      </div>
    </VLoader>
    <div
      v-if="activeTab && activeTab?.expanded"
      class="mt-6 flex"
    >
      <div class="border border-gray-200 max-md:w-full md:min-w-[700px]">
        <VReportTabs
          v-if="isGreaterThenMd"
          no-tabs-gap
          :tabs="innerTabsList"
          :translation-key="translationKey"
          :add-no-pointer-events="innerTabFetching"
          @select="onSelectInnerTab"
        />
        <VReportInnerTabsMobile
          v-else
          :tabs="innerTabsList"
          :active-inner-tab-id="activeInnerTab?.id"
          :translation-key="translationKey"
          @select="onSelectInnerTab"
        />
        <div class="border-t border-gray-200">
          <template v-if="activeInnerTab">
            <VReportTabContent
              :translation-key="translationKey"
              :active-tab="activeTab"
              :active-inner-tab="activeInnerTab"
              :query-options="queryOptions"
              :is-segments-type="isSegmentsType"
              :translation-path-for-list="tabsData?.locale_key"
              @inner-tab-fetching="onInnerTabFetching"
            />
          </template>
          <VReportTabFooter
            :translation-key="translationKey"
            :active-tab="activeTab"
            :query-options="queryOptions"
            :is-segments-type="isSegmentsType"
            :is-structure-states-allowed="isStructureStatesAllowed"
            @change-tab-name="changeTabName"
            @change-structure-state="changeStructureState"
          />
        </div>
      </div>
    </div>
  </div>
</template>
